import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './styles.css';
import { installApiClient } from './lib/apiClient.js';
import { ensureMigrated } from './lib/migrate.js';
import { handleCallbackIfPresent } from './lib/oidc.js';
import ErrorNotice from './components/ErrorNotice.jsx';
import { reportClientError } from './lib/appErrors.js';
import { initializeUiTheme } from './lib/uiTheme.js';
import { initializeDensity } from './lib/uiDensity.js';
import './lib/maplibreWorker.js';

// Apply the saved visual theme before React mounts to avoid a flash of the
// default palette. The default remains the exact pre-design-system theme.
initializeUiTheme();
// Same for density (comfortable / compact): the attribute must be on <html>
// before first paint or compact users see every row jump on mount.
initializeDensity();

// Patch fetch to attach the dev X-User-Id header before anything renders.
installApiClient();

// Dev-only: expose the LIVE store instances for console/e2e debugging.
// Dynamic-importing a store module from the console yields a FRESH module
// (vite HMR timestamps) with its own empty store — this hook is the only
// reliable way to poke the app's real state from outside React.
//
// MUST run AFTER the OIDC callback exchange + App import (see bootstrap): some
// stores transitively create the auth store on import (c2.js → auth.js, whose
// initial `token` reads localStorage). Importing them before the token exchange
// lands would cache a tokenless auth store as a module singleton and strand the
// OIDC login on /login.
function exposeDevStores() {
  if (!import.meta.env.DEV) return;
  Promise.all([
    import('./state/models.js'),
    import('./state/tilesetLibrary.js'),
    import('./state/mappingPlacements.js'),
    import('./state/tagging.js'),
    import('./state/sourceImages.js'),
    import('./lib/tile3dCollider.js'),
    import('./state/c2.js'),
  ]).then(([models, lib, placements, tagging, srcImg, collider, c2]) => {
    window.__twin = {
      useModels: models.useModels,
      useTilesetLibrary: lib.useTilesetLibrary,
      useMappingPlacements: placements.useMappingPlacements,
      useTagging: tagging.useTagging,
      useSourceImages: srcImg.useSourceImages,
      raycastEnu: collider.raycastEnu,
      useC2: c2.useC2,
    };
  });
}

// Kick the one-time IndexedDB → server migration early. It's a memoized
// singleton; the projects slice awaits the same promise before its first
// load, so the dashboard never races ahead of the migration.
ensureMigrated();

window.addEventListener('error', (e) => {
  console.error('[globalops] uncaught:', e.error || e.message);
  reportClientError(e.error || new Error(e.message), {
    source: 'window.error',
    technicalDetail: `${e.filename || ''}:${e.lineno || 0}:${e.colno || 0}\n${e.error?.stack || ''}`,
  });
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[globalops] unhandled rejection:', e.reason);
  reportClientError(e.reason, { source: 'window.unhandledrejection' });
});

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('[globalops] ErrorBoundary caught:', error, info.componentStack);
    const reported = reportClientError(error, {
      source: 'react.error-boundary',
      technicalDetail: `${error?.stack || ''}\n${info.componentStack || ''}`,
    });
    this.setState({ error: reported });
  }
  render() {
    if (this.state.error) {
      return (
        <main className="min-h-screen bg-[var(--color-bg-1)] p-6 text-[var(--color-text)]">
          <div className="mx-auto mt-16 max-w-xl rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-5 shadow-xl">
            <h1 className="mb-3 text-lg font-semibold">GlobalOps needs to recover</h1>
            <ErrorNotice error={this.state.error} />
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md bg-[var(--color-qc-blue)] px-4 py-2 text-sm font-semibold text-white"
            >
              Reload application
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

// Bootstrap. In OIDC mode a redirect back from Keycloak arrives at /login
// carrying ?code&state; handleCallbackIfPresent() completes the token exchange
// and mirrors the access token into localStorage BEFORE we render. App (and
// its transitive auth store, whose initial `token` reads that storage) is
// imported dynamically here — after the exchange — so the freshly-minted token
// is present when the store is first created. In local mode (or a normal load)
// the callback handler is a fast no-op and this reduces to the previous flow.
async function bootstrap() {
  try {
    await handleCallbackIfPresent();
  } catch (e) {
    console.log('[twin-viewer] oidc callback failed:', e?.message || e);
  }
  const { default: App } = await import('./App.jsx');
  createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
  // After render: the auth store now exists with the exchanged token, so the
  // debug hook can safely import stores that touch it (c2.js → auth.js).
  exposeDevStores();
}

bootstrap();
